import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Leaf,
  Users,
  Calendar,
  Sprout,
  Warehouse,
  Smartphone,
  Workflow,
  ShieldCheck,
  Sparkles,
  Check,
  AlertCircle,
} from "lucide-react";
import { IMG } from "../data/content";
import Seo from "../components/Seo";
import api, { formatApiErrorDetail } from "../lib/api";

const SUPPORT_AREAS = [
  {
    no: "01",
    icon: Users,
    title: "Community engagement & governance",
    body: "Listen first. Identify the appropriate decision-makers, permissions, aspirations and rules for participation.",
  },
  {
    no: "02",
    icon: Calendar,
    title: "Seed knowledge & seasonal planning",
    body: "Species priorities, seed calendars, local observations, mother-plant selection and collection planning.",
  },
  {
    no: "03",
    icon: Sprout,
    title: "Training & collection practice",
    body: "Safe, ethical harvesting, genetic diversity, traceability, handling, cleaning, testing and quality protocols.",
  },
  {
    no: "04",
    icon: Warehouse,
    title: "Community seed banks & infrastructure",
    body: "Storage options, seed-lot records, equipment, nurseries, propagation and staged facility planning.",
  },
  {
    no: "05",
    icon: Smartphone,
    title: "Digital collection & data sovereignty",
    body: "Offline-first field records, photos, GPS, optional voice notes and community-defined access permissions.",
  },
  {
    no: "06",
    icon: Workflow,
    title: "Restoration pathways & livelihoods",
    body: "Connect seed availability to nurseries, restoration projects, purchasers, funding partners and potential community-owned enterprises.",
  },
];

const PATHWAY = [
  {
    no: "1",
    title: "Start a conversation",
    body: "Tell us about your group, your interests and what support would be useful. No obligation to begin a project.",
  },
  {
    no: "2",
    title: "Listen and scope",
    body: "Agree who should be involved, which species and opportunities matter, and what permissions and cultural protocols apply.",
  },
  {
    no: "3",
    title: "Design a practical pilot",
    body: "Choose a manageable starting activity: training, a seasonal survey, a collection trial, a seed-lot system or a seed-bank feasibility plan.",
  },
  {
    no: "4",
    title: "Build capability",
    body: "Identify equipment, training, standards, funding and technical partners, with local leadership and paid participation where resourced.",
  },
  {
    no: "5",
    title: "Connect to the movement",
    body: "Where appropriate, establish links with seed banks, nurseries and restoration demand, with clear terms and community control.",
  },
  {
    no: "6",
    title: "Review and grow",
    body: "Review ecological results, community benefit, costs and ongoing support before expansion.",
  },
];

const INTEREST_OPTIONS = [
  { value: "collection-training", label: "Collection training" },
  { value: "seed-bank", label: "Seed bank" },
  { value: "digital-tools", label: "Digital tools" },
  { value: "restoration-supply", label: "Restoration supply" },
  { value: "governance", label: "Governance" },
  { value: "funding-partnerships", label: "Funding & partnerships" },
  { value: "other", label: "Other" },
];

const TOOLS = [
  "Offline collection records",
  "Seed-lot traceability",
  "Images and field notes",
  "Seasonal monitoring",
  "Optional speech-to-text",
  "Community-controlled data permissions",
];

const scrollToForm = () => {
  const el = document.getElementById("start-a-conversation");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function CommunityLedSeedCollection() {
  const [form, setForm] = useState({
    name: "",
    organisation: "",
    email: "",
    phone: "",
    interests: [],
    message: "",
    prefer_phone: false,
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInterest = (val) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(val)
        ? f.interests.filter((i) => i !== val)
        : [...f.interests, val],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", error: "" });
    try {
      await api.post("/stewardship-enquiries", form);
      setStatus({ state: "success", error: "" });
    } catch (err) {
      setStatus({
        state: "error",
        error: formatApiErrorDetail(err?.response?.data?.detail) || "Could not send. Please try again.",
      });
    }
  };

  return (
    <div data-testid="page-community-led-seed-collection">
      <Seo
        title="Community-Led Seed Collection"
        description="SeedKeepers works with Indigenous communities and ranger teams to explore community-led seed collection, seed banking, skills development, governance, partnerships and participation in restoration supply chains — on their own terms."
        path="/community-led-seed-collection"
        image={IMG.handsSeeds}
        type="article"
      />

      {/* ---------- Hero ---------- */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <p className="specimen-label mb-6" data-testid="iss-eyebrow">
              Programs · Work With Us · Community-led
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              Community-Led
              <br />
              <span className="italic text-eucalypt">Seed Collection.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
              Supporting community-led seed collection, cultural authority, practical capability and
              pathways into the native seed and restoration movement.
            </p>
            <p className="mt-6 text-sm md:text-base text-ink/70 leading-relaxed max-w-2xl">
              Native seed collection is more than gathering planting material. It connects knowledge of
              species and seasons with responsibility for Country, local livelihoods and the renewal of
              landscapes. SeedKeepers helps communities explore the tools, training, partnerships and
              infrastructure needed to build seed initiatives on their own terms.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={scrollToForm}
                data-testid="iss-primary-cta"
                className="inline-flex items-center gap-2 rounded-full bg-ochre2 text-ink font-medium px-6 py-3 text-sm md:text-base hover:brightness-95 transition"
              >
                Explore a Seed Partnership <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#how-it-works"
                data-testid="iss-secondary-cta"
                className="inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink font-medium px-6 py-3 text-sm md:text-base hover:bg-ink/5 transition"
              >
                See How It Works
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img
                src={IMG.handsSeeds}
                alt="Hands cradling native seed, Country in the palm"
                className="w-full h-[360px] md:h-[480px] object-cover"
              />
            </div>
            <p className="mt-3 text-xs text-muted italic">
              Country in the hand — the quiet beginning of every seed system.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Why community-led seed systems matter ---------- */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="specimen-label text-chalk/60 mb-3">01 — Why it matters</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              <em className="text-ochre">Community-led</em> seed systems.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base md:text-lg leading-relaxed text-chalk/90">
              Native seed systems depend on people who know the plants, seasonal conditions and places from
              which seed can be responsibly collected. Communities should be involved in decisions, not only
              employed as collectors.
            </p>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-chalk/80">
              Effective seed programs connect cultural responsibility, ecological integrity, technical
              quality and durable local benefits.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Six practical areas of support ---------- */}
      <section className="border-b border-rule" id="support-areas">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="specimen-label mb-3">02 — Six practical areas of support</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-3xl mb-12">
            Where SeedKeepers <em className="text-eucalypt">can help.</em>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {SUPPORT_AREAS.map((a) => {
              const Icon = a.icon;
              return (
                <article
                  key={a.no}
                  data-testid={`iss-support-${a.no}`}
                  className="group rounded-3xl border border-rule bg-card p-6 md:p-7 hover:border-eucalypt/40 hover:shadow-[0_12px_32px_-20px_rgba(44,74,59,0.35)] transition"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs tracking-widest text-muted">{a.no}</span>
                    <Icon className="w-5 h-5 text-eucalypt" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-serif text-xl md:text-2xl leading-tight text-ink">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-ink/75 leading-relaxed">{a.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- How a partnership can begin (6-step pathway) ---------- */}
      <section className="border-b border-rule bg-parchment" id="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="specimen-label mb-3">03 — How a partnership can begin</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-3xl mb-14">
            A six-step <em className="text-eucalypt">pathway.</em>
          </h2>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {PATHWAY.map((step) => (
              <li
                key={step.no}
                data-testid={`iss-step-${step.no}`}
                className="rounded-2xl border border-rule bg-card p-6 md:p-7"
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-4xl md:text-5xl text-ochre2 leading-none">
                    {step.no}
                  </span>
                  <h3 className="font-serif text-xl md:text-2xl leading-tight text-ink">
                    {step.title}
                  </h3>
                </div>
                <p className="mt-4 text-sm md:text-base text-ink/75 leading-relaxed">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---------- Trust callouts ---------- */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          <div
            data-testid="iss-callout-custodians"
            className="rounded-3xl border border-eucalypt/20 bg-eucalypt/5 p-7 md:p-10"
          >
            <ShieldCheck className="w-7 h-7 text-eucalypt" strokeWidth={1.5} />
            <h3 className="mt-5 font-serif text-2xl md:text-3xl leading-tight text-ink">
              Seed knowledge belongs with its custodians.
            </h3>
            <p className="mt-4 text-sm md:text-base text-ink/80 leading-relaxed">
              Participation does not require communities to disclose restricted knowledge, sacred
              information or sensitive collection sites. Communities determine what is recorded, what may
              be shared, who can access it and whether it can be used for research, AI analysis or
              commercial purposes. Consent for one use does not authorise another.
            </p>
            <p className="mt-4 text-xs md:text-sm text-ink/60 italic leading-relaxed">
              SeedKeepers does not acquire ownership of Indigenous knowledge, data or biological materials.
            </p>
          </div>
          <div
            data-testid="iss-callout-not-every-seed"
            className="rounded-3xl border border-ochre2/30 bg-ochre2/10 p-7 md:p-10"
          >
            <Leaf className="w-7 h-7 text-ochre2" strokeWidth={1.5} />
            <h3 className="mt-5 font-serif text-2xl md:text-3xl leading-tight text-ink">
              Not every seed has to become a product.
            </h3>
            <p className="mt-4 text-sm md:text-base text-ink/80 leading-relaxed">
              Some seed initiatives will prioritise cultural continuity, local food plants, medicinal
              plants, conservation or community access. Others may pursue restoration supply contracts or
              seed enterprises. These are different, legitimate outcomes — sales are not the default
              measure of success.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- Tools that can support the work ---------- */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <p className="specimen-label text-chalk/60 mb-3">04 — Future-facing capability</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
              Tools that can <em className="text-ochre">support the work.</em>
            </h2>
            <p className="mt-6 text-sm md:text-base text-chalk/80 leading-relaxed">
              AI-supported identification or interpretation is described here as{" "}
              <strong className="text-chalk">possible future assistance</strong> requiring validation,
              consent and appropriate governance — not a capability we claim is in production today.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {TOOLS.map((t) => (
                <li
                  key={t}
                  className="flex items-start gap-3 rounded-xl border border-chalk/15 bg-chalk/5 px-5 py-4"
                >
                  <Sparkles className="w-4 h-4 text-ochre mt-1 flex-shrink-0" strokeWidth={1.5} />
                  <span className="text-sm md:text-base text-chalk/90 leading-snug">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- Enquiry CTA + form ---------- */}
      <section id="start-a-conversation" className="border-b border-rule">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="specimen-label mb-3">05 — Start a conversation</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
            Could a seed initiative work for <em className="text-eucalypt">your community?</em>
          </h2>
          <p className="mt-6 text-base md:text-lg text-ink/80 leading-relaxed max-w-3xl">
            Whether you are beginning with an idea or already collecting native seed, SeedKeepers can
            discuss possible next steps, resources and partnerships. We begin with your priorities and
            the authority of your community.
          </p>

          {status.state === "success" ? (
            <div
              data-testid="iss-form-success"
              className="mt-10 rounded-3xl border border-eucalypt/30 bg-eucalypt/10 p-8 md:p-10"
            >
              <Check className="w-8 h-8 text-eucalypt" strokeWidth={1.5} />
              <h3 className="mt-5 font-serif text-2xl md:text-3xl text-ink leading-tight">
                Thank you. Your enquiry has been received.
              </h3>
              <p className="mt-4 text-base md:text-lg text-ink/80 leading-relaxed">
                We will contact you to discuss whether and how SeedKeepers may be able to assist.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="iss-form"
              className="mt-10 rounded-3xl border border-rule bg-card p-6 md:p-10 space-y-6"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-xs tracking-widest font-mono text-muted">CONTACT NAME *</span>
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="iss-field-name"
                    className="mt-2 w-full border-b border-ink/20 bg-transparent py-2.5 text-base focus:outline-none focus:border-eucalypt transition"
                  />
                </label>
                <label className="block">
                  <span className="text-xs tracking-widest font-mono text-muted">
                    ORGANISATION / GROUP (OPTIONAL)
                  </span>
                  <input
                    type="text"
                    value={form.organisation}
                    onChange={(e) => update("organisation", e.target.value)}
                    data-testid="iss-field-org"
                    className="mt-2 w-full border-b border-ink/20 bg-transparent py-2.5 text-base focus:outline-none focus:border-eucalypt transition"
                  />
                </label>
                <label className="block">
                  <span className="text-xs tracking-widest font-mono text-muted">EMAIL</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="iss-field-email"
                    className="mt-2 w-full border-b border-ink/20 bg-transparent py-2.5 text-base focus:outline-none focus:border-eucalypt transition"
                  />
                </label>
                <label className="block">
                  <span className="text-xs tracking-widest font-mono text-muted">PHONE</span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    data-testid="iss-field-phone"
                    className="mt-2 w-full border-b border-ink/20 bg-transparent py-2.5 text-base focus:outline-none focus:border-eucalypt transition"
                  />
                </label>
              </div>

              <fieldset>
                <legend className="text-xs tracking-widest font-mono text-muted mb-3">
                  WHAT ARE YOU INTERESTED IN? (SELECT ANY)
                </legend>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map((opt) => {
                    const active = form.interests.includes(opt.value);
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        onClick={() => toggleInterest(opt.value)}
                        data-testid={`iss-interest-${opt.value}`}
                        className={`rounded-full border px-4 py-2 text-sm transition ${
                          active
                            ? "bg-eucalypt text-chalk border-eucalypt"
                            : "border-ink/20 text-ink/80 hover:border-eucalypt/50"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <label className="block">
                <span className="text-xs tracking-widest font-mono text-muted">SHORT MESSAGE</span>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  data-testid="iss-field-message"
                  className="mt-2 w-full border border-ink/15 rounded-xl bg-transparent p-3 text-base focus:outline-none focus:border-eucalypt transition"
                />
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.prefer_phone}
                  onChange={(e) => update("prefer_phone", e.target.checked)}
                  data-testid="iss-field-prefer-phone"
                  className="mt-1 w-4 h-4 accent-eucalypt"
                />
                <span className="text-sm text-ink/80">
                  I would prefer an initial phone conversation.
                </span>
              </label>

              <div
                data-testid="iss-privacy-note"
                className="flex items-start gap-3 rounded-xl bg-ochre2/10 border border-ochre2/30 px-4 py-3"
              >
                <AlertCircle className="w-4 h-4 text-ochre2 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                <p className="text-xs md:text-sm text-ink/80 leading-relaxed">
                  Please do not include restricted cultural knowledge or precise locations of sensitive
                  species in this form.
                </p>
              </div>

              {status.state === "error" && (
                <p
                  data-testid="iss-form-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
                >
                  {status.error}
                </p>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={status.state === "submitting"}
                  data-testid="iss-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-ochre2 text-ink font-medium px-6 py-3 text-sm md:text-base hover:brightness-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "submitting" ? "Sending…" : "Start a Conversation"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ---------- Related pathways ---------- */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <p className="specimen-label mb-6">Related</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              { to: "/training", label: "Training & Resources" },
              { to: "/services", label: "Seed Collection Tools" },
              { to: "/infrastructure", label: "Community Seed Banks" },
              { to: "/contact", label: "Restoration Partnerships" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`iss-related-${l.to.slice(1)}`}
                className="group rounded-2xl border border-rule bg-card px-5 py-4 flex items-center justify-between hover:border-eucalypt/40 transition"
              >
                <span className="font-serif text-base md:text-lg text-ink">{l.label}</span>
                <ArrowRight className="w-4 h-4 text-ink/60 group-hover:translate-x-0.5 group-hover:text-eucalypt transition" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
